import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FutureTabContent } from "@/components/patients/FutureTabContent";
import { VaccinationFormDialog } from "@/components/applications/ApplicationFormDialog";
import { buildUpcomingContextFromRow, type UpcomingApplicationDialogContext } from "@/components/applications/upcoming-dialog-context";
import { AppointmentDetailsDialog } from "@/components/calendar/AppointmentDetailsDialog";
import { useFutureRows } from "@/hooks/useFutureRows";
import { ChevronDown, ClipboardList } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface AttendanceCarePanelProps {
  petId: string;
  ownerId?: string | null;
  clinicId: string | null;
  includeGrooming?: boolean;
}

export function AttendanceCarePanel({
  petId,
  ownerId = null,
  clinicId,
  includeGrooming = true,
}: AttendanceCarePanelProps) {
  const { t } = useTranslation("patients");
  const [selectedUpcomingItem, setSelectedUpcomingItem] = useState<UpcomingApplicationDialogContext | null>(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);
  const { pendingRows, upcomingRows, isLoading } = useFutureRows({
    scope: "patient",
    petId,
    ownerId,
    clinicId,
    includeGrooming,
    includeLinkedAppointmentRows: true,
  });

  const careContent = (
    <CardContent className="px-3 pb-3">
      <FutureTabContent
        showToolbar={false}
        pendingRows={pendingRows}
        upcomingRows={upcomingRows}
        isLoading={isLoading}
        showClinicHint={!clinicId}
        compact
        hideSectionDescriptions
        tableMaxHeightClassName="max-h-[200px]"
        pendingEmptyMessage={t("futureTab.emptyPendingPatient")}
        upcomingEmptyMessage={t("futureTab.emptyPatient")}
        onVaccinationSelect={(vaccination) => {
          setSelectedUpcomingItem(buildUpcomingContextFromRow({ petId, vaccination }));
        }}
        onPlanSelect={(plan) => {
          setSelectedUpcomingItem(buildUpcomingContextFromRow({ petId, plan }));
        }}
        onAppointmentSelect={(row) => {
          if (!row.appointmentId) return;
          setSelectedAppointmentId(row.appointmentId);
        }}
      />
    </CardContent>
  );

  return (
    <>
      <Card className="hidden lg:block">
        <CardHeader className="pb-0">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardList className="h-4 w-4" />
            {t("petProfile.tabs.appointments")}
          </CardTitle>
        </CardHeader>
        {careContent}
      </Card>

      <Collapsible className="lg:hidden">
        <Card>
          <CardHeader className="p-4 lg:p-3 lg:pb-0">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="group flex w-full items-center justify-between gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
              >
                <CardTitle className="flex items-center gap-2 text-lg">
                  <ClipboardList className="h-4 w-4" />
                  {t("petProfile.tabs.appointments")}
                </CardTitle>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
              </button>
            </CollapsibleTrigger>
          </CardHeader>
          <CollapsibleContent>
            {careContent}
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {selectedAppointmentId ? (
        <AppointmentDetailsDialog
          appointmentId={selectedAppointmentId}
          open={Boolean(selectedAppointmentId)}
          onOpenChange={(open) => {
            if (!open) setSelectedAppointmentId(null);
          }}
        />
      ) : null}

      {selectedUpcomingItem && clinicId ? (
        <VaccinationFormDialog
          open={Boolean(selectedUpcomingItem)}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedUpcomingItem(null);
            }
          }}
          petId={petId}
          clinicId={clinicId}
          upcomingContext={selectedUpcomingItem}
          initialTab="record"
          showCreateAppointmentToggle
        />
      ) : null}
    </>
  );
}
